import { useState } from "react";
import { Play, Film, ExternalLink, Trophy, Sparkles } from "lucide-react";

const SHOWREEL = [
  {
    id: "vision",
    label: "The Vision · Utility Slayer",
    tag: "Director's Cut",
    accent: "#D4AF37",
    url: "https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/3efd0cc9151648a9865895ccb5384643_utility_slayer_v4_directors_cut.mp4",
    blurb:
      "The manifesto. Why Wingman Alfred exists — and what it kills for you.",
  },
  {
    id: "onboarding",
    label: "The Onboarding · 7-Hour Handshake",
    tag: "Recruit Walkthrough",
    accent: "#60A5FA",
    url: "https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/cb8b1baec5734ceaad1fe5620c0f76af_output.mp4",
    blurb:
      "The first 7 hours. How a recruit becomes a Consistent Creator on day one.",
  },
  {
    id: "field",
    label: "The Field Support · Alfred Service Director",
    tag: "In-Field Ops",
    accent: "#10B981",
    url: "https://customer-assets.emergentagent.com/wingman/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/attachments/ce2a19eab1b54186b28beb9441fdceb6_alfred_master_FINAL_v3.mp4",
    blurb:
      "The real-time Service Director in action — from ticket to signed job.",
  },
  {
    id: "bentley",
    label: "The Future · Bentley Mascot IP",
    tag: "Character IP",
    accent: "#F59E0B",
    url: "https://customer-assets-4nw71qhi.emergentagent.net/jobs/27422a56-9ef6-4b54-bcf6-0131f0c1fa3c/videos/31481dff275ace52.mp4",
    blurb:
      "Bentley the Otter — the family-facing IP that turns every guest into a fan.",
  },
];

const VOTE_URL = "https://app.emergent.sh/showcase/wingman-academy";

export default function AlfredShowreel() {
  const [activeId, setActiveId] = useState(SHOWREEL[0].id);
  const active = SHOWREEL.find((s) => s.id === activeId) || SHOWREEL[0];

  return (
    <div className="space-y-8" data-testid="alfred-showreel">
      {/* Contest CTA banner */}
      <div
        className="relative overflow-hidden rounded-sm border p-6 flex flex-col md:flex-row md:items-center gap-4"
        style={{
          borderColor: "rgba(212,175,55,0.55)",
          background:
            "linear-gradient(90deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.05) 55%, rgba(6,11,20,0.7) 100%)",
          boxShadow: "0 0 40px -18px rgba(212,175,55,0.7)",
        }}
        data-testid="alfred-showreel-vote-banner"
      >
        <div
          className="absolute inset-x-0 top-0 h-px"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(212,175,55,0.9), transparent)",
          }}
        />
        <div
          className="w-11 h-11 rounded-sm border flex items-center justify-center shrink-0"
          style={{
            borderColor: "rgba(212,175,55,0.6)",
            background: "rgba(212,175,55,0.14)",
          }}
        >
          <Trophy size={20} style={{ color: "#D4AF37" }} />
        </div>
        <div className="flex-1 min-w-0">
          <div
            className="text-[10px] uppercase tracking-[0.42em] mb-1 flex items-center gap-2"
            style={{ color: "#D4AF37" }}
          >
            <Sparkles size={10} /> Emergent Showcase · Community Vote
          </div>
          <div className="font-display text-2xl sm:text-3xl text-white leading-tight">
            Wingman Alfred is on the ballot.
          </div>
          <div className="text-slate-300 text-xs mt-1">
            Cast your vote — one click keeps the Utility Slayer climbing.
          </div>
        </div>
        <a
          href={VOTE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-gold px-6 py-3 rounded-sm text-sm inline-flex items-center gap-2 shrink-0"
          data-testid="alfred-showreel-vote-button"
        >
          VOTE FOR WINGMAN ALFRED <ExternalLink size={14} />
        </a>
      </div>

      {/* Section header */}
      <div className="flex items-center gap-3">
        <span
          className="h-px flex-1 max-w-[64px]"
          style={{
            background:
              "linear-gradient(90deg, rgba(212,175,55,0.55), transparent)",
          }}
        />
        <div className="text-[10px] uppercase tracking-[0.34em] text-slate-400 inline-flex items-center gap-2">
          <Film size={11} /> The Alfred Experience
        </div>
        <span
          className="h-px flex-1"
          style={{
            background:
              "linear-gradient(90deg, transparent, rgba(212,175,55,0.22))",
          }}
        />
      </div>

      {/* Player + list */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3">
          <div
            className="rounded-sm overflow-hidden border relative"
            style={{
              borderColor: `${active.accent}44`,
              background: "rgba(6,11,20,0.7)",
              boxShadow: `0 0 44px -22px ${active.accent}88`,
            }}
            data-testid="alfred-showreel-player-frame"
          >
            <video
              key={active.id}
              src={active.url}
              controls
              playsInline
              preload="metadata"
              className="w-full aspect-video bg-black"
              data-testid="alfred-showreel-player"
            />
            <div className="p-4 border-t border-white/5">
              <div
                className="text-[10px] uppercase tracking-[0.28em] mb-1"
                style={{ color: active.accent }}
              >
                {active.tag}
              </div>
              <div className="font-display text-xl text-white leading-tight">
                {active.label}
              </div>
              <div className="text-slate-400 text-xs mt-1.5">{active.blurb}</div>
            </div>
          </div>
        </div>

        <ul className="lg:col-span-2 space-y-3" data-testid="alfred-showreel-list">
          {SHOWREEL.map((s) => {
            const isActive = s.id === active.id;
            return (
              <li key={s.id}>
                <button
                  onClick={() => setActiveId(s.id)}
                  className="w-full text-left rounded-sm border p-3 transition-all group"
                  style={{
                    borderColor: isActive ? `${s.accent}88` : "rgba(255,255,255,0.08)",
                    background: isActive
                      ? `linear-gradient(180deg, ${s.accent}12 0%, rgba(6,11,20,0.7) 100%)`
                      : "rgba(6,11,20,0.6)",
                    boxShadow: isActive ? `0 0 24px -14px ${s.accent}88` : "none",
                  }}
                  data-testid={`alfred-showreel-item-${s.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-9 h-9 rounded-sm border flex items-center justify-center shrink-0"
                      style={{
                        borderColor: `${s.accent}55`,
                        background: `${s.accent}18`,
                      }}
                    >
                      <Play size={14} style={{ color: s.accent }} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-[9px] uppercase tracking-[0.28em] mb-0.5"
                        style={{ color: s.accent }}
                      >
                        {s.tag}
                      </div>
                      <div className="font-display text-sm text-white leading-snug">
                        {s.label}
                      </div>
                      <div className="text-[11px] text-slate-400 mt-1 line-clamp-2">
                        {s.blurb}
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
